import { type CSSProperties, useLayoutEffect, useRef, useState } from 'react'

// Tailwind 3.1 has no line-clamp utility, so the clamp is inline (same as PinnedNotesBanner).
const clampStyle: CSSProperties = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
}

/**
 * A call note in a grid cell.
 *
 * Collections notes run long, and a whole one in a table cell stretches its row until the grid
 * stops being scannable. So the cell shows the first two lines and grows only when asked: row
 * heights stay uniform while you read down the outcome and PTP columns, and the full note is one
 * click away in place, with no modal to open and close for every row worth reading.
 *
 * Whether anything is actually hidden is measured, not guessed from the text length, because how
 * much fits depends on the column width. It is re-measured on resize, which also covers the first
 * paint: measuring once on mount reports an overflow that the web font then removes, and a note
 * that fits ends up wearing a Show more that reveals nothing.
 */
export const NotesCell = ({ text }: { text?: string }) => {
  const [expanded, setExpanded] = useState(false)
  const [isClamped, setClamped] = useState(false)
  const ref = useRef<HTMLParagraphElement>(null)
  // Read by the observer, which is set up once and must not capture a stale `expanded`.
  const expandedRef = useRef(false)
  expandedRef.current = expanded

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    // Only measured while collapsed. Expanded, nothing overflows by definition, and recording that
    // would drop the toggle for a frame on the way back down.
    const measure = () => {
      if (!expandedRef.current) setClamped(el.scrollHeight > el.clientHeight + 1)
    }
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [text])

  if (!text) return null

  return (
    <div className="max-w-sm min-w-[12rem]">
      <p
        ref={ref}
        className="whitespace-pre-wrap break-words"
        style={expanded ? undefined : clampStyle}
        // The whole note on hover, so a quick check costs no click at all.
        title={text}
      >
        {text}
      </p>
      {(isClamped || expanded) && (
        <button
          type="button"
          className="text-[11px] text-shade-blue hover:underline mt-[2px]"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  )
}
